import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@workspace/ui/components/button'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { useMemo, useState } from 'react'
import {
  type AdminNormalizeInvestorPreferencesData,
  useAdminNormalizeInvestorPreferencesMutation,
  useAdminUnknownInvestorPreferencesQuery,
  useInvestorConfigQuery,
} from '../../../hooks/api'

export const Route = createFileRoute('/admin/investors/normalize-preferences')({
  component: NormalizeInvestorPreferencesPage,
})

interface EnumOption {
  value: string
  label: string
}

interface ParseResult {
  dictionary: Record<string, string>
  error: string | null
}

function parseDictionary(input: string, allowedTargets: Set<string>): ParseResult {
  const trimmed = input.trim()
  if (!trimmed) return { dictionary: {}, error: null }
  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch (e) {
    return { dictionary: {}, error: `Invalid JSON: ${(e as Error).message}` }
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { dictionary: {}, error: 'Expected a JSON object (e.g. {"AI": "ai_ml"}).' }
  }
  const dict: Record<string, string> = {}
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value !== 'string') {
      return {
        dictionary: {},
        error: `Value for "${key}" must be a string (got ${typeof value}).`,
      }
    }
    if (value !== '' && !allowedTargets.has(value)) {
      return {
        dictionary: {},
        error: `Target "${value}" for key "${key}" is not a canonical enum code. Use "" to drop or one of the valid codes listed below.`,
      }
    }
    dict[key] = value
  }
  return { dictionary: dict, error: null }
}

function NormalizeInvestorPreferencesPage() {
  const unknownQuery = useAdminUnknownInvestorPreferencesQuery()
  const configQuery = useInvestorConfigQuery()
  const normalize = useAdminNormalizeInvestorPreferencesMutation()

  const [areaJson, setAreaJson] = useState('{}')
  const [regionJson, setRegionJson] = useState('{}')
  const [preview, setPreview] = useState<AdminNormalizeInvestorPreferencesData | null>(null)

  const unknown = unknownQuery.data
  const areaOptions = configQuery.data?.investmentAreas ?? []
  const regionOptions = configQuery.data?.investmentRegions ?? []

  const areaTargets = useMemo(() => new Set(areaOptions.map((o) => o.value)), [areaOptions])
  const regionTargets = useMemo(() => new Set(regionOptions.map((o) => o.value)), [regionOptions])

  const areaParse = useMemo(() => parseDictionary(areaJson, areaTargets), [areaJson, areaTargets])
  const regionParse = useMemo(
    () => parseDictionary(regionJson, regionTargets),
    [regionJson, regionTargets]
  )

  const hasParseError = areaParse.error !== null || regionParse.error !== null

  const runPreview = () => {
    if (hasParseError) return
    setPreview(null)
    normalize.mutate(
      {
        areaDictionary: areaParse.dictionary,
        regionDictionary: regionParse.dictionary,
        dryRun: true,
      },
      { onSuccess: (resp) => setPreview(resp) }
    )
  }

  const runApply = () => {
    if (hasParseError) return
    if (!preview || preview.changed === 0) return
    if (
      !window.confirm(
        `Apply changes to ${preview.changed} investor(s)? ${preview.cleared} will lose at least one preference field entirely. This cannot be undone.`
      )
    )
      return
    normalize.mutate(
      {
        areaDictionary: areaParse.dictionary,
        regionDictionary: regionParse.dictionary,
        dryRun: false,
      },
      {
        onSuccess: (resp) => {
          setPreview(resp)
        },
      }
    )
  }

  const fillFromUnknown = (
    rows: { value: string }[],
    current: string,
    setter: (v: string) => void
  ) => {
    let base: Record<string, string> = {}
    try {
      const parsed = JSON.parse(current.trim() || '{}')
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        base = parsed as Record<string, string>
      }
    } catch {
      // ignore — overwrite
    }
    for (const r of rows) {
      if (!(r.value in base)) base[r.value] = ''
    }
    setter(JSON.stringify(base, null, 2))
  }

  const isLoading = unknownQuery.isLoading || configQuery.isLoading
  if (isLoading) {
    return <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
  }

  if (!unknown) {
    return <div className="py-12 text-center text-sm text-red-400">Failed to load.</div>
  }

  return (
    <div className="w-full space-y-6">
      <div className="rounded-lg border border-border bg-card/40 p-4">
        <h2 className="mb-1 text-base font-semibold">Normalize investor preferences</h2>
        <p className="text-sm text-muted-foreground">
          Write a JSON dictionary mapping free-form values to canonical enum codes below. Empty
          target (<code>""</code>) means &quot;drop&quot;. Any free-form value missing from the
          dictionary is dropped too. After <strong>Apply</strong>, every investor&apos;s preferences
          contain only canonical codes.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <UnknownReference
          title="Unknown areas in DB"
          rows={unknown.areas}
          onFill={() => fillFromUnknown(unknown.areas, areaJson, setAreaJson)}
        />
        <UnknownReference
          title="Unknown regions in DB"
          rows={unknown.regions}
          onFill={() => fillFromUnknown(unknown.regions, regionJson, setRegionJson)}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <CanonicalReference title="Canonical area codes" options={areaOptions} />
        <CanonicalReference title="Canonical region codes" options={regionOptions} />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <DictionaryEditor
          title="Area dictionary (JSON)"
          value={areaJson}
          onChange={setAreaJson}
          error={areaParse.error}
          placeholder={'{\n  "AI": "ai_ml",\n  "Crypto": "web3",\n  "Big Data": ""\n}'}
        />
        <DictionaryEditor
          title="Region dictionary (JSON)"
          value={regionJson}
          onChange={setRegionJson}
          error={regionParse.error}
          placeholder={'{\n  "US East": "north_america",\n  "EMEA": "europe"\n}'}
        />
      </div>

      <div className="flex items-center gap-3">
        <CtaButton
          onClick={runPreview}
          disabled={hasParseError}
          loading={normalize.isPending && normalize.variables?.dryRun}
        >
          Preview changes
        </CtaButton>
        <CtaButton
          onClick={runApply}
          disabled={!preview || preview.changed === 0 || hasParseError}
          loading={normalize.isPending && normalize.variables?.dryRun === false}
        >
          {preview ? `Apply ${preview.changed} change${preview.changed === 1 ? '' : 's'}` : 'Apply'}
        </CtaButton>
      </div>

      {normalize.isError && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">
          Failed: {(normalize.error as Error).message}
        </div>
      )}

      {preview && <PreviewResult data={preview} />}
    </div>
  )
}

interface UnknownReferenceProps {
  title: string
  rows: { value: string; investorCount: number }[]
  onFill: () => void
}

function UnknownReference({ title, rows, onFill }: UnknownReferenceProps) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title} ({rows.length})
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onFill}
          disabled={rows.length === 0}
          className="h-6 text-[11px]"
        >
          Seed JSON
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-3 text-xs text-muted-foreground">None.</div>
      ) : (
        <div className="max-h-[180px] overflow-y-auto">
          <table className="w-full text-xs">
            <tbody className="divide-y divide-border">
              {rows.map((row) => (
                <tr key={row.value}>
                  <td className="px-4 py-1 font-mono">{row.value}</td>
                  <td className="px-4 py-1 text-right tabular-nums text-muted-foreground">
                    {row.investorCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function CanonicalReference({ title, options }: { title: string; options: EnumOption[] }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="border-b border-border bg-muted/30 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      <div className="max-h-[180px] overflow-y-auto">
        <table className="w-full text-xs">
          <tbody className="divide-y divide-border">
            {options.map((opt) => (
              <tr key={opt.value}>
                <td className="px-4 py-1 font-mono">{opt.value}</td>
                <td className="px-4 py-1 text-muted-foreground">{opt.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface DictionaryEditorProps {
  title: string
  value: string
  onChange: (next: string) => void
  error: string | null
  placeholder: string
}

function DictionaryEditor({ title, value, onChange, error, placeholder }: DictionaryEditorProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        spellCheck={false}
        className="h-48 w-full rounded-md border border-border bg-background p-3 font-mono text-xs"
      />
      {error && (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs text-red-300">
          {error}
        </div>
      )}
    </div>
  )
}

function PreviewResult({ data }: { data: AdminNormalizeInvestorPreferencesData }) {
  return (
    <div className="space-y-4 rounded-lg border border-border bg-card/40 p-4">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span
          className={`rounded-md border px-2 py-0.5 text-xs font-medium uppercase ${
            data.dryRun
              ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
              : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
          }`}
        >
          {data.dryRun ? 'Preview' : 'Applied'}
        </span>
        <span>
          Total: <strong>{data.totalInvestors}</strong>
        </span>
        <span>
          Changed: <strong>{data.changed}</strong>
        </span>
        <span>
          Unchanged: <strong>{data.unchanged}</strong>
        </span>
        <span className={data.cleared > 0 ? 'text-amber-400' : ''}>
          Cleared: <strong>{data.cleared}</strong>
        </span>
      </div>

      {(data.droppedAreaValues.length > 0 || data.droppedRegionValues.length > 0) && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
          <div className="mb-1 font-semibold text-amber-300">Values that will be dropped</div>
          {data.droppedAreaValues.length > 0 && (
            <div>
              <span className="text-muted-foreground">Areas: </span>
              <span className="font-mono">{data.droppedAreaValues.join(', ')}</span>
            </div>
          )}
          {data.droppedRegionValues.length > 0 && (
            <div>
              <span className="text-muted-foreground">Regions: </span>
              <span className="font-mono">{data.droppedRegionValues.join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {data.changes.length > 0 && (
        <div className="overflow-hidden rounded-md border border-border">
          <div className="border-b border-border bg-muted/30 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Per-investor diff ({data.changes.length})
          </div>
          <div className="max-h-[400px] divide-y divide-border overflow-y-auto">
            {data.changes.map((c) => (
              <div key={c.id} className="px-3 py-2 text-xs">
                <div className="mb-1 font-medium">{c.name}</div>
                {!sameArray(c.beforeAreas, c.afterAreas) && (
                  <div className="font-mono">
                    <span className="text-muted-foreground">areas: </span>
                    <span className="text-red-400">{JSON.stringify(c.beforeAreas)}</span>
                    <span> → </span>
                    <span className="text-emerald-400">{JSON.stringify(c.afterAreas)}</span>
                  </div>
                )}
                {!sameArray(c.beforeRegions, c.afterRegions) && (
                  <div className="font-mono">
                    <span className="text-muted-foreground">regions: </span>
                    <span className="text-red-400">{JSON.stringify(c.beforeRegions)}</span>
                    <span> → </span>
                    <span className="text-emerald-400">{JSON.stringify(c.afterRegions)}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function sameArray(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}
